import { FollowerTemplateSchema, type FollowerTemplate } from '../../schemas/follower';

/**
 * Template della **Banda di Guerra del Barbaro** (manuale V1, rr. 2070-2096).
 *
 * 5° template Follower del sistema unificato FI-8 (dopo Compagno Animale,
 * Famiglio, Supporto Elementale, Animare Zombi). Primo template di
 * `type: 'collective'`: i PF rappresentano il numero di membri attivi della
 * Banda (5 di base, 8 con Khan dei Khan), non punti vita standard. Il
 * Carico è `'narrative'` (GM definisce in base alla fiction).
 *
 * **Pattern stress-testati a beneficio dello schema D8b**:
 *  - **`type: 'collective'`** (prima istanza)
 *  - **`load: 'narrative'`** (prima istanza)
 *  - **`upgrades`** (D8b Q1=A): *Il Khan dei Khan* (advanced_6_10) modifica
 *    la stessa Banda — level 2→3, HP 5→8, mod +2→+3 sulla stessa stat.
 *  - **`fixedTraits` description-only**: 4 capacità peculiari della Banda
 *    (subisce danno → -1 PF, recupera → +1 PF, infligge danno = PF attuali,
 *    "sono tanti!"). Effects runtime non strutturati — tracciati in FI-40
 *    per Fase F (modalità giocabile).
 *
 * **Stats fisse** (manuale r. 2074):
 *  - Livello: 2 (Khan dei Khan: 3)
 *  - Lealtà: 2
 *  - PF: 5 (= membri attivi; Khan dei Khan: 8)
 *  - Mod: +2 a 1 stat scelta (Khan: +3) + +1 a tutti gli altri
 *  - Carico: 'narrative' (GM-defined, fiction-based)
 *  - Dado di Danno: d6 (implicito; il manuale non lo cita ma è il default
 *    base manuale r. 1271)
 *  - Costo: «Soddisfare uno dei tuoi appetiti» (manuale r. 2078)
 *
 * **Note di design**:
 *  - Niente `recruitRoll`: la mossa *Reclutare* del PG produce il PF
 *    iniziale (10+ tutti, 7-9 metà, 6- 0) ma è una mossa runtime al tavolo,
 *    non un setup di build-time. Description-only nel testo della mossa.
 *  - Niente `archetypeTagBundle`: la Banda non ha varianti di archetype.
 *  - Niente `primarySkillFromNarrativeField`: la Banda non ha Abilità preset
 *    (è un gregario "collettivo" senza mestiere singolo).
 */
export const barbaroBandaDiGuerraTemplate: FollowerTemplate = FollowerTemplateSchema.parse({
  id: 'barbaro-banda-di-guerra',
  sourceMoveId: 'barbaro.banda-di-guerra',
  type: 'collective',
  label: 'Banda di Guerra',
  baseStats: {
    level: 2,
    modifiers: {
      forza: 1,
      destrezza: 1,
      costituzione: 1,
      intelligenza: 1,
      saggezza: 1,
      carisma: 1,
    },
    hpRule: { type: 'fixed', value: 5 },
    damageDie: 'd6',
    load: 'narrative',
    tags: [],
  },
  fixedSkills: [],
  // 4 capacità peculiari description-only (FI-40: effects runtime per Fase F).
  fixedTraits: [
    {
      id: 'banda-danno-fisso-subito',
      label: 'Subisce danno → -1 PF',
      description: 'Quando subisce danno, non importa quanto, sottrai 1 solo PF.',
    },
    {
      id: 'banda-cura-fissa',
      label: 'Recupera PF → +1 PF',
      description: 'Quando recupera PF, non importa quanti, recupera 1 solo PF.',
    },
    {
      id: 'banda-danno-pari-pf',
      label: 'Infligge danno = PF attuali',
      description: 'Quando infligge danno, infligge danni pari ai suoi PF attuali (non un dado).',
    },
    {
      id: 'banda-sono-tanti',
      label: 'Sono tanti!',
      description: 'E il GM lo sa…',
    },
  ],
  defaultLoyalty: 2,
  costMotivation: 'Soddisfare uno dei tuoi appetiti',
  modifierBonus: { fromChoiceGroup: 'modificatore', amount: 2 },
  nameFromNarrativeField: 'nome-banda',
  // D8b: upgrade strutturale via Il Khan dei Khan (advanced_6_10).
  upgrades: [
    {
      fromMoveId: 'barbaro.il-khan-dei-khan',
      levelOverride: 3,
      hpRuleOverride: { type: 'fixed', value: 8 },
      modifierBonusOverride: { fromChoiceGroup: 'modificatore', amount: 3 },
    },
  ],
  respawnRule:
    'La Banda si ricompone via la mossa *Reclutare* del Barbaro (10+ recupera tutti i PF e cambia la stat con bonus; 7-9 recupera metà PF; 6- 0 PF e complicazione GM).',
});
